import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, ArrowRight, Loader2, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const StoriesGrid = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedStories();
  }, []);

  const fetchPublishedStories = async () => {
    try {
      const response = await axios.get(`${API}/stories/?status=Published`);
      // Get latest 6 stories (can overlap with carousel - showing all published stories)
      setStories(response.data.data?.slice(0, 6) || []);
    } catch (err) {
      console.error('Error fetching published stories:', err);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  if (loading) {
    return (
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mb-4">
              Latest Startup Stories
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover inspiring journeys, funding insights, and lessons from founders building the future.
            </p>
          </div>
          <div className="flex justify-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 sm:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mb-4">
            Latest Startup Stories
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover inspiring journeys, funding insights, and lessons from founders building the future.
          </p>
        </div>

        {stories.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p className="text-lg">No stories published yet</p>
            <p className="text-sm mt-2">Check back soon for inspiring startup stories!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {stories.map((story, index) => (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                <Card className="group overflow-hidden hover:shadow-card-hover transition-all duration-300 flex flex-col h-full">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={story.banner_photo || 'https://images.unsplash.com/photo-1557804506-669a67965ba0'}
                      alt={story.title || story.startup_name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge variant="category">{story.submission_type || 'Startup Story'}</Badge>
                    </div>
                  </div>

                  <CardHeader>
                    <CardTitle className="text-xl font-heading group-hover:text-primary transition-colors line-clamp-2">
                      {story.title || story.startup_name}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">
                      {story.short_description || story.brief_info?.substring(0, 100) + '...'}
                    </CardDescription>
                  </CardHeader>

                  <CardFooter className="mt-auto flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(story.submitted_at || story.created_at)}</span>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="group/btn"
                      onClick={() => window.location.href = `/story/${story.slug}`}
                    >
                      Read
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                    </Button>
                  </CardFooter>
                </Card>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.location.href = '/stories'}
              >
                View All Stories
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
